import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, UserRound } from "lucide-react";
import { scrollToId, PHONE_DISPLAY, PHONE_TEL, PHONE2_DISPLAY, PHONE2_TEL } from "@/lib/site";

const LINKS = [
  { label: "Manifesto", id: "manifesto" },
  { label: "Services", id: "services" },
  { label: "Service History", id: "history" },
  { label: "Booking", id: "booking" },
  { label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const go = (id) => {
    setOpen(false);
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 450);
      return;
    }
    scrollToId(id);
  };

  return (
    <header className="fixed top-0 z-50 w-full border-b border-white/10 bg-[#0A0C10]/80 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-5 lg:px-6 xl:px-8">
        <button onClick={() => window.__lenis?.scrollTo(0)} className="flex shrink-0 items-center gap-3" data-testid="nav-logo">
          <span className="font-display text-2xl tracking-[0.15em] text-slate-50">SAGITTARIUS</span>
          <span className="hidden whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 sm:block lg:hidden xl:block">
            Motor Works
          </span>
        </button>

        <nav className="hidden items-center gap-4 lg:flex xl:gap-6">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 transition-colors duration-200 hover:text-amber-500 xl:text-xs xl:tracking-[0.2em]"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-3 lg:flex">
          <div className="hidden flex-col items-end whitespace-nowrap font-mono text-[11px] leading-tight tracking-wider text-slate-400 xl:flex">
            <a href={PHONE_TEL} data-testid="nav-phone" className="transition-colors hover:text-slate-100">{PHONE_DISPLAY}</a>
            <a href={PHONE2_TEL} data-testid="nav-phone-secondary" className="transition-colors hover:text-slate-100">{PHONE2_DISPLAY}</a>
          </div>
          <Link
            to="/portal"
            data-testid="nav-portal-link"
            className="flex items-center gap-2 whitespace-nowrap border border-white/15 px-4 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-slate-300 transition-colors duration-200 hover:border-amber-600/60 hover:text-amber-500"
          >
            <UserRound size={13} /> <span className="hidden xl:inline">My Portal</span><span className="xl:hidden">Portal</span>
          </Link>
          <button
            onClick={() => go("booking")}
            data-testid="nav-book-cta"
            className="group flex items-center gap-2 whitespace-nowrap border border-amber-600/60 bg-amber-600/10 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-amber-500 transition-all duration-200 hover:bg-amber-600 hover:text-black"
          >
            Book Now
            <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button className="text-slate-200 lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-white/10 bg-[#0A0C10] lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className="py-3 text-left font-mono text-sm uppercase tracking-[0.2em] text-slate-300 hover:text-amber-500"
                >
                  {l.label}
                </button>
              ))}
              <Link to="/portal" data-testid="nav-mobile-portal-link" className="py-3 text-left font-mono text-sm uppercase tracking-[0.2em] text-amber-500">
                My Portal
              </Link>
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-4 font-mono text-xs tracking-wider text-slate-400">
                <a href={PHONE_TEL} data-testid="nav-mobile-phone">{PHONE_DISPLAY}</a>
                <a href={PHONE2_TEL} data-testid="nav-mobile-phone-secondary">{PHONE2_DISPLAY}</a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
